import { FC, ReactNode, useMemo } from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { InfoCircle, PlusCircle } from "react-bootstrap-icons";
import { Link } from "react-router-dom";

import { CopyableText } from "src/components/copyableText.tsx";
import { DaysOfDelay } from "src/components/subscriptionList/daysOfDelay.tsx";
import { subscriptionColumns } from "src/components/subscriptionList/subscriptionListColumns.ts";
import { usePaginationContext } from "src/components/subscriptionList/subscriptionListPaginationContext.ts";
import { SubscriptionListRow } from "src/components/subscriptionList/subscriptionListRow.tsx";
import { HeaderColumn } from "src/components/table/headerColumn.tsx";
import { Container, Subscription, SubscriptionSortBy } from "src/model/subscription.ts";
import { useUserState } from "src/store/store.ts";
import { iseaReferenceLink } from "src/utils/externalReferences.ts";
import { buildPortString } from "src/utils/ports.ts";
import { formatDate, formatDateTime } from "src/utils/timeFormat.ts";
import { useBackUrl } from "src/utils/useBackUrl.ts";

interface SubscriptionListProps {
  subscriptions: Subscription[];
  columns: (keyof SubscriptionRow)[];
}

export interface SubscriptionRow {
  id: string;
  shipmentId: string | null;
  done: boolean;
  shippingLine: string;
  daysOfDelay: ReactNode;
  billOfLading: ReactNode;
  bookingNumber: ReactNode;
  externalReference: ReactNode;
  shippersReference: ReactNode;
  portOfLoading: string | null;
  etdPortOfLoading: string | null;
  portOfDischarge: string | null;
  etaPortOfDischarge: ReactNode;
  containers: Container[];
}

const iseaReferenceRegex = new RegExp("^\\w{1,3}-(\\w{1,3}-)?\\d{4}-\\d{5}$");

export const SubscriptionList: FC<SubscriptionListProps> = ({ subscriptions, columns }) => {
  const user = useUserState();
  const { pagination, filter, setPagination } = usePaginationContext();
  const { backUrl } = useBackUrl();

  const buildExternalReferenceList = (referenceList: (string | null)[]): ReactNode => {
    const items: ReactNode[] = [];
    for (const referenceListElement of referenceList) {
      if (referenceListElement === null) {
        continue;
      }

      if (items.length > 0) {
        items.push(<br key={`reference-break-${items.length}`} />);
      }

      if (iseaReferenceRegex.test(referenceListElement)) {
        items.push(
          <a href={iseaReferenceLink(referenceListElement)} target="_blank" key={`reference-${items.length}`}>
            <CopyableText text={referenceListElement} />
          </a>
        );
      } else {
        items.push(
          <span key={`reference-${items.length}`}>
            <CopyableText text={referenceListElement} />
          </span>
        );
      }
    }

    return <>{items}</>;
  };

  const buildEtaPortOfDischarge = (subscription: Subscription): ReactNode => {
    if (subscription.etaPortOfDischargeHistory.length > 0) {
      return (
        <OverlayTrigger
          placement="right"
          delay={250}
          overlay={
            <Tooltip>
              {subscription.etaPortOfDischargeHistory.map((h, index) => (
                <div key={`history-${index}`} className="mb-2 text-start">
                  <b>ETA: {formatDateTime(h.arrival)}</b>
                  <br />
                  <small>reported on {formatDate(h.validAt)}</small>
                </div>
              ))}
            </Tooltip>
          }
        >
          <span className="d-inline-flex align-items-center">
            {formatDateTime(subscription.etaPortOfDischarge)}&nbsp;&nbsp;
            <InfoCircle />
          </span>
        </OverlayTrigger>
      );
    } else {
      return (
        <span className="d-inline-flex align-items-center">{formatDateTime(subscription.etaPortOfDischarge)}</span>
      );
    }
  };

  const buildShippersReferenceList = (subscription: Subscription): ReactNode => {
    return (
      <div>
        {subscription.metaData
          .map((md) => md.shippersReference)
          .filter((r) => r !== null)
          .map((sr, index) => (
            <div key={`sr-${index}`}>
              <CopyableText text={sr} />
            </div>
          ))}
      </div>
    );
  };

  const subscriptionRows: SubscriptionRow[] = useMemo(() => {
    return subscriptions.map(
      (s): SubscriptionRow => ({
        id: s.id,
        shipmentId: s.shipmentId,
        done: s.done,
        shippingLine: `${s.shippingLineName} (${s.scac})`,
        daysOfDelay: <DaysOfDelay days={s.daysOfDelay} />,
        billOfLading: <CopyableText text={s.billOfLading} />,
        bookingNumber: <CopyableText text={s.bookingNumber} />,
        portOfLoading: buildPortString(s.portOfLoading, s.portOfLoadingCode),
        portOfDischarge: buildPortString(s.portOfDischarge, s.portOfDischargeCode),
        shippersReference: buildShippersReferenceList(s),
        externalReference: buildExternalReferenceList(s.metaData.map((md) => md.externalReference)),
        etaPortOfDischarge: buildEtaPortOfDischarge(s),
        etdPortOfLoading: formatDateTime(s.etdPortOfLoading),
        containers: s.containers,
      })
    );
  }, [subscriptions]);

  const onSortChange = (sortKey: SubscriptionSortBy | undefined) => {
    if (sortKey === undefined) {
      return;
    }

    if (pagination.sortBy === sortKey) {
      setPagination((prevState) => ({
        ...prevState,
        pagination: { ...prevState.pagination, sortDir: prevState.pagination.sortDir === "Asc" ? "Desc" : "Asc" },
      }));
    } else {
      setPagination((prevState) => ({
        ...prevState,
        pagination: { ...prevState.pagination, sortBy: sortKey, sortDir: "Asc" },
      }));
    }
  };

  if (subscriptionRows.length === 0) {
    if (filter.query !== undefined) {
      return <div>No subscriptions found for search.</div>;
    }

    return (
      <div>
        No subscriptions found. You can add one by clicking{" "}
        <Link to="/subscriptions/new" className="text-black">
          here
        </Link>
        .
      </div>
    );
  }

  const orderedAndFilteredColumns = subscriptionColumns
    .filter((cc) => columns.includes(cc.key))
    .toSorted((a, b) => columns.indexOf(a.key) - columns.indexOf(b.key));

  return (
    <Table className="align-middle">
      <thead>
        <tr>
          {orderedAndFilteredColumns.map((c) => (
            <th key={`th-${c.key}`}>
              <HeaderColumn
                value={c.value}
                sortKey={c.sortKey}
                onSortChange={onSortChange}
                selectedSortKey={pagination.sortBy}
                selectedSortDir={pagination.sortDir}
              />
            </th>
          ))}
          {user.role === "Admin" || user.rights.includes("CreateSubscription") ? (
            <>
              <th style={{ width: "32px" }} className="text-end" />
              <th style={{ width: "32px" }} className="text-end">
                <Link to="/subscriptions/new" state={{ backUrl: backUrl }} className="text-black">
                  <PlusCircle size={18} />
                </Link>
              </th>
            </>
          ) : (
            <>
              <th />
            </>
          )}
        </tr>
      </thead>
      <tbody className="table-group-divider">
        {subscriptionRows.map((sr, index) => (
          <SubscriptionListRow
            subscriptionRow={sr}
            key={`subscription-${index}`}
            orderedAndFilteredColumns={orderedAndFilteredColumns}
          />
        ))}
      </tbody>
    </Table>
  );
};
